import { type KeyboardDownCapture } from '@canvas/input-types/keyboard-events-types';

import { type SelectionsMachine } from '../../../machine/selection-machine';
import { type SharedEventLoop } from '../../event-loop';
import { type SelectionInput } from '../../types';
import { type TableNavigationAdapter } from '../table-navigation-adapter';
import { isEditableTarget } from '../utils/is-editable-target';

import { createTableNavigationKeyDownHandler } from './handle-table-navigation-key-down';

/**
 * Creates the table arrow/navigation keyboard input.
 *
 * This input subscribes to the synchronous keyboard loop so that
 * `preventDefault` runs in the same event-dispatch frame as the native
 * keydown. Without this, Cmd+Arrow would fire browser back/forward
 * navigation before the RAF-batched handler could cancel it.
 */
export function createTableNavigationInput(
  machine: SelectionsMachine,
  sharedLoop: SharedEventLoop,
  adapter: TableNavigationAdapter
): SelectionInput {
  const handler = createTableNavigationKeyDownHandler(adapter);
  let unsubscribe: (() => void) | undefined;

  return {
    setup() {
      unsubscribe = sharedLoop.subscribeSyncKeyboardDown(
        (capture: KeyboardDownCapture) => {
          if (capture.properties.consumed) {
            return;
          }

          if (isEditableTarget(capture.properties.event.target)) {
            return;
          }

          handler(capture, machine);
        }
      );
    },

    dispose() {
      unsubscribe?.();
    }
  };
}
